import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { DroneManufacturerService } from '../../core/data-services/drone-manufacturer.service';
import { DroneManufacturerRequest } from '../../core/request-models/drone-manufacturer-request.model';
import { optionalText } from '../../core/utils/form-value-utils';

@Component({
  selector: 'app-manufacturer-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './manufacturer-form.component.html',
  styleUrl: './manufacturer-form.component.scss'
})
export class ManufacturerFormComponent implements OnInit {
  manufacturerForm: FormGroup;
  loading = false;
  loadingManufacturer = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  manufacturerId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private droneManufacturerService: DroneManufacturerService
  ) {
    this.manufacturerForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      url: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)]],
      logoUrl: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)]]
    });
  }

  ngOnInit(): void {
    this.manufacturerId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.manufacturerId && this.manufacturerId !== 'create';

    if (this.isEditMode) {
      this.loadManufacturer();
    }
  }

  async loadManufacturer(): Promise<void> {
    if (!this.manufacturerId) return;
    this.loadingManufacturer = true;

    try {
      const manufacturer = await firstValueFrom(this.droneManufacturerService.getManufacturer(this.manufacturerId));
      if (manufacturer) {
        this.manufacturerForm.patchValue({
          name: manufacturer.name ?? '',
          description: manufacturer.description ?? '',
          url: manufacturer.url ?? '',
          logoUrl: manufacturer.logoUrl ?? ''
        });
      }
    } catch (error) {
      console.error('Failed to load manufacturer:', error);
      this.errorMessage = 'Failed to load manufacturer details';
    } finally {
      this.loadingManufacturer = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.manufacturerForm.invalid) {
      this.manufacturerForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formData = this.manufacturerForm.value;

      if (this.isEditMode && this.manufacturerId) {
        await firstValueFrom(this.droneManufacturerService.updateManufacturer(this.manufacturerId, this.toRequest()));

        this.successMessage = 'Manufacturer updated successfully';

        setTimeout(() => {
          this.router.navigate(['/drone-catalog', this.manufacturerId]);
        }, 1500);
      } else {
        await firstValueFrom(this.droneManufacturerService.createManufacturer(this.toRequest()));

        this.router.navigate(['/success'], {
          state: {
            entities: [
              { type: 'manufacturer', action: 'created', name: formData.name },
            ],
            message: 'Drone manufacturer created successfully',
            details: {
              'Name': formData.name,
              'Website': formData.url || 'Not provided',
              'Description': formData.description ? 'Provided' : 'Not provided'
            },
            createAnotherRoute: '/drone-catalog/create'
          }
        });
      }
    } catch (error) {
      console.error('Save manufacturer error:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? (this.isEditMode
        ? 'Failed to update manufacturer. Please try again.'
        : 'Failed to create manufacturer. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate(['/drone-catalog', this.manufacturerId]);
    } else {
      this.router.navigate(['/drone-catalog']);
    }
  }

  /** The form as the hub's writable fields; emptied optional text becomes `null` (clear). */
  private toRequest(): DroneManufacturerRequest {
    const formData = this.manufacturerForm.value;
    return {
      name: formData.name,
      description: optionalText(formData.description),
      url: optionalText(formData.url),
      logoUrl: optionalText(formData.logoUrl),
    };
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Manufacturer' : 'Create Manufacturer';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Manufacturer';
  }
}
